import { useEffect, useRef, useState } from "react";
import { Form, FormikProvider, useFormik } from "formik";
import { useRecoilRefresher_UNSTABLE, useRecoilValueLoadable } from "recoil";
import * as Yup from "yup";
// material
import {
  Alert,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";

import useIsMountedRef from "@hooks/useIsMountedRef";
import axios from "@utils/axios";
import { emailField } from "@utils/emailRule";
import { storageAvailable } from "@utils/storage";
import { SUPPORT_EMAIL, supportMailto } from "@utils/support";
import { publicSettingsQuery } from "@states/publicSettings";
import IdentityFields, { EMPTY_IDENTITY, IdentityValues } from "./IdentityFields";
import { failureMessage } from "./failureMessage";
import { forgetEmail, readRememberedEmail, rememberAfterLogin } from "./rememberedEmail";

/**
 * 로그인 링크를 요청한다. **여기서는 세션이 열리지 않는다.**
 *
 * 서버는 가입자든 아니든 같은 응답(202)을 같은 시간에 준다. 그래서 이 함수는
 * «보냈다» 말고는 알 수 있는 것이 없고, 화면도 그 이상을 말하지 않는다 —
 * «그런 계정이 없습니다» 라고 알려 주면 그것만으로 가입자 명단을 긁을 수 있다.
 */
type SubmitIdentity = { txSeqNo: string; telNo: string; otpNo: string };

const requestMagicLink = async (email: string, identity: SubmitIdentity | null) => {
  await axios({
    method: "post",
    url: "auth/magic/request",
    // 인증을 안 받았으면 **아예 안 보낸다.** 빈 문자열을 보내면 서버가 «셋 중
    // 하나만 왔다» 로 읽는데, 그건 부분적으로 채워 검증을 건너뛰려는 시도와
    // 구분되지 않는다.
    data: identity
      ? {
          email,
          txSeqNo: identity.txSeqNo,
          telNo: identity.telNo.replace(/\D/g, ""),
          otpNo: identity.otpNo,
        }
      : { email },
  });
};

/** 인증번호 발송. 로그인 화면은 계정이 이미 있으므로 `/auth/identity/sms` 를 쓴다. */
const sendLoginOtp = async (values: IdentityValues, agreedTerms: string[]) => {
  const response = await axios({
    method: "post",
    url: "auth/identity/sms",
    data: {
      name: values.name.trim(),
      regFront: values.regFront,
      regRear: values.regRear,
      telComCd: values.telComCd,
      telNo: values.telNo.replace(/\D/g, ""),
      // 서버가 다시 본다. 화면에서만 막으면 API 를 직접 부르는 순간 통과하는데,
      // 서버는 KCB 에 `agree1~4` 를 전부 `Y` 로 보낸다 — 받지 않은 동의를 받았다고
      // 선언하게 된다. **인수를 필수로 둬서** 빠뜨리면 tsc 가 멈춘다.
      agreedTerms,
    },
  });
  return response.data.txSeqNo as string;
};

export default function LoginForm({ onSignupRequest, onFindEmail }: {
  /** 회원가입 신청 화면으로 */
  onSignupRequest?: () => void;
  /** 이메일 찾기 화면으로 */
  onFindEmail?: () => void;
}) {
  const isMountedRef = useIsMountedRef();

  /** 링크를 보낸 주소. 값이 있으면 «보냈습니다» 화면을 띄운다. */
  const [sentTo, setSentTo] = useState("");

  // 기억해 둔 주소가 있으면 그 사람은 전에 «기억하기» 를 켠 것이다. 체크 상태도
  // 같이 살려 둬야 한다 — 안 그러면 다음 로그인에서 조용히 잊어버린다.
  const [remembered] = useState(readRememberedEmail);

  // 사이트 데이터 저장이 막힌 브라우저는 세션을 둘 곳이 없어 **로그인이 원래 안 된다**.
  // 그걸 먼저 말한다 — 안 그러면 링크를 받아도 들어올 수 없고 이유도 모른다(f1-9).
  const [canStore] = useState(storageAvailable);

  // 본인인증이 켜져 있으면 **미리** 알려 준다. 켜진 줄 모르고 링크를 누른 사람은
  // 갑자기 나타난 휴대폰 인증 화면에서 «내가 뭘 잘못했나» 를 먼저 의심한다.
  // 꺼져 있으면 이 안내도 사라져야 한다 — 없는 절차를 예고하면 그것대로 겁을 준다.
  const publicSettings = useRecoilValueLoadable(publicSettingsQuery);
  /**
   * ★ 설정은 **실패할 때마다 다시 읽는다.**
   *
   * 화면을 연 뒤에 관리자가 게이트를 켜거나 끄면 이 화면은 그걸 모른다. 켜졌는데
   * 모르면 이메일만 보내다 `IDENTITY_REQUIRED` 로 막히고, 꺼졌는데 모르면 인증번호가
   * «필요하지 않습니다» 로 거절되는데 링크 버튼은 계속 잠겨 있다 — 둘 다 새로고침
   * 전까지 **빠져나갈 길이 없었다**(2026-09-23 수동 TC GL-09). 조회가 한 번 실패해
   * «꺼짐» 으로 떨어진 경우도 같다.
   *
   * 어떤 실패에서든 다시 읽는다. 문장으로 원인을 가리는 것보다 싸고, 값이 그대로면
   * 아무것도 안 바뀐다.
   */
  const refreshSettings = useRecoilRefresher_UNSTABLE(publicSettingsQuery);
  // 다시 읽는 동안은 **마지막으로 알던 값**을 쓴다. 그 사이 «꺼짐» 으로 보이면 인증칸이
  // 사라졌다 나타나면서 동의 체크가 풀린다(관리자 화면 `IdentityPanel` 과 같은 처방).
  const lastKnown = useRef(false);
  if (publicSettings.state === "hasValue") {
    lastKnown.current = publicSettings.contents.identityOnLogin;
  }
  const identityOnLogin = lastKnown.current;
  const [remember, setRemember] = useState(() => remembered !== "");

  // 게이트가 켜졌을 때만 쓰는 값들. 꺼져 있으면 화면에 아예 안 그린다 —
  // 필요 없는 절차를 보여 주면 그것만으로 포기하는 사람이 생긴다.
  const [identity, setIdentity] = useState<IdentityValues>(EMPTY_IDENTITY);
  const [txSeqNo, setTxSeqNo] = useState("");
  const [sending, setSending] = useState(false);
  const [otpError, setOtpError] = useState("");

  const sendOtp = async (agreedTerms: string[]) => {
    setOtpError("");
    setSending(true);
    try {
      setTxSeqNo(await sendLoginOtp(identity, agreedTerms));
    } catch (error: any) {
      setOtpError(
        failureMessage(error, {
          fallback: "인증번호를 보내지 못했습니다. 잠시 후 다시 시도해주세요.",
          identityRequired: "인증번호를 보내지 못했습니다. 잠시 후 다시 시도해주세요.",
        }),
      );
      // 게이트가 꺼진 뒤라면 «필요하지 않습니다» 로 거절된다. 다시 읽으면 인증칸이
      // 사라지고 링크 버튼이 풀린다.
      refreshSettings();
    } finally {
      if (isMountedRef.current) setSending(false);
    }
  };

  // 서버와 같은 조건으로 먼저 막는다 — `gumin@naver` 를 통과시키면 서버가 422 를 주고
  // 사람은 «잠시 후 다시 시도» 를 봤다(f1-4). 규칙은 `utils/emailRule.ts`.
  const LoginSchema = Yup.object().shape({
    email: emailField("이메일이 유효하지 않습니다."),
  });

  const formik = useFormik({
    initialValues: { email: remembered, afterSubmit: "" },
    validationSchema: LoginSchema,
    onSubmit: async (values, { setErrors, setSubmitting }) => {
      try {
        await requestMagicLink(
          values.email,
          identityOnLogin && txSeqNo
            ? { txSeqNo, telNo: identity.telNo, otpNo: identity.otpNo }
            : null,
        );
        // **로그인이 실제로 된 뒤에** 기억한다(`MagicLink` 가 확정한다). 202 는 «보냈다» 가
        // 아니다 — 서버는 없는 주소에도 같은 답을 준다. 여기서 바로 기억하면 오타 주소가
        // 다음에도 그대로 채워졌다(f1-6).
        if (remember) rememberAfterLogin(values.email);
        else forgetEmail();
        if (isMountedRef.current) {
          setSentTo(values.email);
          setSubmitting(false);
        }
      } catch (error: any) {
        if (isMountedRef.current) {
          setSubmitting(false);
          // 서버가 준 이유를 **그대로** 보여 준다. 예전에는 429 말고는 전부 «잠시 후
          // 다시» 로 덮어서, 인증번호를 틀린 사람이 번호를 고치는 대신 기다렸다가
          // 같은 번호로 또 눌렀다(2026-09-23 수동 TC GL-04). 이 문은 계정을 응답
          // 뒤에 조회하므로 여기 오는 이유는 가입 여부와 상관이 없다 —
          // `failureMessage` 머리글에 근거를 적었다.
          //
          // (예전 조건의 `error?.status` 는 **늘 비어 있었다.** 인터셉터가 붙이는
          // 이름은 `httpStatus` 다. «잦» 이 문장에 들어 있어서 우연히 걸렀을 뿐이다.)
          setErrors({
            afterSubmit: failureMessage(error, {
              fallback: "메일을 보내지 못했습니다. 잠시 후 다시 시도해주세요.",
              identityRequired:
                "로그인에 본인인증이 필요합니다. 본인인증을 마친 뒤 다시 눌러주세요. " +
                "본인인증 칸이 보이지 않으면 화면을 새로고침해주세요.",
            }),
          });
        }
        refreshSettings();
      }
    },
  });

  const { errors, touched, isSubmitting, handleSubmit, getFieldProps } = formik;

  /**
   * 게이트가 **켜져 있다가 꺼지면** 인증의 흔적을 지운다.
   *
   * 인증칸은 사라졌는데 «인증번호가 일치하지 않습니다» 가 남아 있으면, 칸도 없는
   * 인증번호를 고치라는 말이 된다. 받아 둔 거래번호도 버린다 — 다시 켜졌을 때 죽은
   * 번호로 요청이 나가지 않게.
   *
   * 반대로 **꺼져 있다가 켜질 때는 지우지 않는다.** 그때 뜬 «본인인증이 필요합니다»
   * 가 바로 사용자가 봐야 할 안내다.
   */
  const wasIdentityOn = useRef(identityOnLogin);
  useEffect(() => {
    if (wasIdentityOn.current && !identityOnLogin) {
      setTxSeqNo("");
      setOtpError("");
      setIdentity(EMPTY_IDENTITY);
      formik.setErrors({});
    }
    wasIdentityOn.current = identityOnLogin;
    // `formik` 은 렌더마다 새 객체라 넣으면 매번 돈다. 보는 것은 게이트 값 하나다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identityOnLogin]);

  if (sentTo) {
    return (
      <Stack spacing={3}>
        <Alert severity="success">
          <b>{sentTo}</b> 로 로그인 링크를 보냈습니다.
        </Alert>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          메일의 <b>로그인하기</b> 버튼을 눌러주세요. 링크는 15분 동안 유효하며 한 번만
          사용할 수 있습니다.
          <br/>
          메일이 보이지 않으면 스팸함도 확인해주세요.
          {/* 본인인증을 거쳐 보냈으면, 계정 이름과 인증한 이름이 다를 때 서버는 메일을
              **조용히** 안 보낸다(«보냈습니다» 는 같다 — 가입 여부를 숨기려고). 그걸 모르면
              사람은 다시 인증하고 다시 누른다 — 누를 때마다 문자 한 통(f1-2). */}
          {identityOnLogin && (
            <>
              <br/>
              본인인증한 분의 이름이 계정에 등록된 이름과 다르면 메일이 가지 않습니다. 메일이
              오지 않으면{" "}
              <Link href={supportMailto("[프리즘 BTS] 로그인 링크 메일 문의")}>{SUPPORT_EMAIL}</Link>
              로 문의해주세요.
            </>
          )}
        </Typography>
        <Button
          fullWidth
          size="large"
          variant="outlined"
          onClick={() => {
            // 쓴 거래번호·인증번호를 남기면 다음 요청이 «이미 인증이 완료된 거래입니다» 로
            // 실패하는데, 칸이 채워져 있어 버튼은 바로 눌린다(f1-6). 인증번호는 다시 받는다.
            // 이름·생년월일·번호는 그대로 둔다 — 다시 칠 이유가 없다.
            setTxSeqNo("");
            setIdentity((previous) => ({ ...previous, otpNo: "" }));
            setOtpError("");
            setSentTo("");
          }}
        >
          다른 주소로 다시 받기
        </Button>
      </Stack>
    );
  }

  return (
    <FormikProvider value={formik}>
      {/* `autoComplete="off"` 를 쓰지 않는다. 그게 브라우저와 비밀번호 관리자의
          **아이디 저장·자동완성을 통째로 막고** 있었다. 비밀번호가 없어진 지금
          여기서 저장될 수 있는 것은 이메일 주소뿐이라 감출 이유도 없다.
          아래 «이메일 기억하기» 는 브라우저를 안 쓰거나 못 믿는 사람을 위한
          두 번째 층이다 — 둘은 서로를 대신하지 않는다. */}
      <Form noValidate onSubmit={handleSubmit}>
        <Stack spacing={3}>
          {!canStore && (
            <Alert severity="warning">
              이 브라우저는 사이트 데이터 저장이 막혀 있어 로그인할 수 없습니다. 브라우저 설정에서
              이 사이트의 데이터(쿠키·사이트 데이터) 저장을 허용한 뒤 다시 열어주세요.
            </Alert>
          )}
          {/* 안내는 **한 줄뿐이다.**

              «비밀번호는 필요하지 않습니다» 를 뺐다. 비밀번호 칸이 화면에 아예
              없으므로 그 말은 **없는 것을 설명하는 문장**이다. 읽는 사람에게는
              «어? 비밀번호가 있었나» 를 한 번 더 생각하게 만들 뿐이다.

              placeholder 로 넣지 않는 이유 — MUI outlined + `label` 조합에서
              placeholder 는 **포커스로 라벨이 올라간 뒤에만** 보이고, 한 글자 치는
              순간 사라진다. 정작 망설이는 순간에 없다. helperText 는 계속 떠 있고
              `aria-describedby` 로 묶여 스크린리더도 이 칸의 설명으로 읽는다. */}
          <TextField
            fullWidth
            autoComplete="username"
            type="email"
            label="이메일"
            {...getFieldProps("email")}
            error={Boolean(touched.email && errors.email)}
            helperText={
              (touched.email && errors.email) ||
              "입력하신 메일로 로그인 링크를 보내드립니다."
            }
            // 기본 12px 은 이 화면에서 **가장 작은 글씨**인데, 담고 있는 것은
            // 가장 중요한 안내다. 순서가 거꾸로다. 14px 로 올린다.
            FormHelperTextProps={{ sx: { fontSize: "0.875rem" } }}
          />

          {/* 게이트가 켜지면 **이 화면에서** 본인인증을 마친다. 예전에는 메일을
              받고 링크를 누른 뒤에야 인증 화면이 나왔는데, 그러면 메일을 기다리는
              중간에 흐름이 끊긴다. 이제 여기서 끝내고 링크는 누르기만 하면 된다.

              이메일 **아래**에 둔다. 사람은 «무엇으로 로그인하는가» 를 먼저 정하고
              그 다음에 «나를 증명» 한다. 인증 칸이 먼저 오면 이메일 칸을 못 보고
              «여기가 어디지» 부터 묻게 된다. */}
          {identityOnLogin && (
            <>
              <Alert severity="info">
                <b>본인인증</b>을 마치면 로그인 링크를 보내드립니다.
              </Alert>
              <IdentityFields
                values={identity}
                onChange={setIdentity}
                sent={Boolean(txSeqNo)}
                sending={sending}
                onRequestOtp={sendOtp}
                error={otpError}
                disabled={isSubmitting}
              />
            </>
          )}

          {errors.afterSubmit && (
            <Alert severity="error">{errors.afterSubmit}</Alert>
          )}

          {/* 기본은 **꺼짐**이다. 이 화면은 공용 PC 에서도 열리고, 이메일은
              개인정보다. 켜는 것은 그 사람이 정한다. */}
          <FormControlLabel
            control={
              <Checkbox
                checked={remember}
                onChange={(event) => {
                  setRemember(event.target.checked);
                  // 끄면 **그 자리에서** 지운다. 다음 로그인까지 기다리지 않는다 —
                  // 껐다고 생각하고 자리를 뜨는 사람이 있다.
                  if (!event.target.checked) forgetEmail();
                }}
              />
            }
            label={
              <Typography variant="body2">
                이메일 기억하기
                {/* 부가설명은 **켰을 때만** 띄운다. 끄고 있는 사람에게는 알려 줄
                    것이 없고, 늘 떠 있으면 그만큼 읽을 것이 는다. */}
                {remember && (
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{ color: "text.secondary", ml: 1 }}
                  >
                    이 기기에만 저장됩니다
                  </Typography>
                )}
              </Typography>
            }
            sx={{ mt: -1, mb: -1 }}
          />

          {/* 게이트가 켜졌으면 인증번호까지 넣어야 누를 수 있다. 눌렀다가
              «인증을 먼저 하세요» 를 보는 것보다, 못 누르는 편이 덜 헤맨다. */}
          <LoadingButton
            fullWidth
            size="large"
            type="submit"
            variant="contained"
            loading={isSubmitting}
            disabled={identityOnLogin && (!txSeqNo || identity.otpNo.length === 0)}
          >
            로그인 링크 받기
          </LoadingButton>
        </Stack>

        <Stack sx={{ my: 3 }} spacing={3}>
          <Divider/>
          {/* 두 문을 **같은 크기로 나란히** 둔다.

              «계정이 없다» 와 «주소를 잊었다» 는 둘 다 로그인 못 하는 사람이 여기서
              고르는 길이고, 한쪽만 크게 두면 다른 쪽이 안 보인다. 비밀번호가 있던
              시절에는 «비밀번호를 잊어버리셨나요?» 가 작은 글씨였는데, 정작 그것을
              찾는 사람이 가장 급한 사람이었다.

              스스로 계정을 만들 수는 없다. `POST /api/auth/register` 는 관리자 RBAC
              으로 막혀 있고 가입은 승인으로만 열린다. 그래서 "회원가입" 이 아니라
              "회원가입 신청" 이다. */}
          <Stack direction="row" spacing={2}>
            <Button
              fullWidth
              size="large"
              variant="outlined"
              onClick={() => onSignupRequest && onSignupRequest()}
            >
              회원가입 신청
            </Button>
            <Button
              fullWidth
              size="large"
              variant="outlined"
              onClick={() => onFindEmail && onFindEmail()}
            >
              이메일을 잊으셨나요?
            </Button>
          </Stack>
        </Stack>
      </Form>
    </FormikProvider>
  );
}
